import { find, first, forEach, includes, isEqual, toLower } from "lodash";
import { useTranslation } from "react-i18next";
import { useDictionary } from "./useDictionary";
import { DimText } from "@/shared/services/api/graphql/generated";
import { DimensionContext } from "@/types/data";
import {
  DimensionWithScores,
  getTopStrengthDimensions,
} from "@/features/people/hooks/useDimensionsWithScores";
import {
  getIsCompetenciesDimension,
  getIsPersonalityDimension,
  getIsValuesDimension,
} from "@/shared/utils/selectors/getDimensionTypes";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";

// Text selector logic documentation https://assessio.atlassian.net/wiki/x/AYDLq

export const getDimTextBucketValueFromScore = (
  score: number,
  includeMid: boolean = false
) => {
  if (includeMid) {
    if (score < 35) return "low";
    if (score > 65) return "high";
    return "mid";
  }

  if (score < 50) return "low";
  return "high";
};

const getDimensionTag = (text?: DimText) => {
  // Generate a special markdown format that we can intercept
  // Using a unique identifier that react-markdown will treat as text
  if (!text) return "";
  return `[DIMENSION_HIGHLIGHT:${text.dim_id}:${text.text}]`;
};

const getPersonMentionTag = (dimension: DimensionWithScores) => {
  const personScore =
    find(
      dimension.peopleScores,
      (s) => s?.person?.id === dimension?.score.obj_id
    ) || ({} as any);
  const person = personScore?.person || {};

  if (!person.id || !person.name) return "";
  return `[PERSON:${person.id}:${person.name}]`;
};

export const getDictionaryTerms = (
  dictionary: ReadonlyArray<DimText | null | undefined> | undefined,
  selector: string,
  dimId: number,
  bucket: string
) => {
  if (!dictionary?.length) return [];
  return dictionary
    .filter((term): term is DimText => Boolean(term))
    .filter(
      (t) =>
        t?.dim_id === dimId &&
        isEqual(t?.type, selector) &&
        bucket === t?.bucket &&
        !!t?.text &&
        t?.text !== "N/A"
    );
};

const isBetween = (value: number, min: number, max: number) => {
  return value >= min && value <= max;
};

const canShowStrengthAndRiskTexts = (
  dimension: DimensionWithScores,
  context: DimensionContext
) => {
  const valueForBucketCheck =
    context === "user_in_team"
      ? dimension.score?.value || 0
      : dimension.textsScore?.value || 0;

  if (
    getIsValuesDimension(dimension.type as AssessmentSurveyType) &&
    isBetween(valueForBucketCheck, 30, 70)
  )
    return false;

  return !!dimension.textsScore?.key;
};

const getDimensionDetailsTexts = (
  dictionary: ReadonlyArray<DimText | null | undefined> | undefined,
  dimensions: DimensionWithScores[],
  context: DimensionContext
) => {
  const texts = {
    strength: [] as DimText[],
    risk: [] as DimText[],
    behavior: [] as DimText[],
  };

  forEach(dimensions, (dim) => {
    const textBucket = getDimTextBucketValueFromScore(
      context === "user_in_team"
        ? dim.score?.value || 0
        : dim.textsScore?.value || 0
    );
    const strengthTerms = getDictionaryTerms(
      dictionary,
      "strength",
      dim.id,
      textBucket
    ) as DimText[];

    const riskTerms = getDictionaryTerms(
      dictionary,
      "risk",
      dim.id,
      textBucket
    ) as DimText[];

    // When there is no textsScore, we use the high bucket for competencies
    // This happens when we show the competency details without scores or team benchmark
    const behaviorTerms =
      (textBucket === "high" || !dim.textsScore?.key) &&
      getIsCompetenciesDimension(dim.group as AssessmentSurveyType)
        ? (getDictionaryTerms(
            dictionary,
            "competency_behavior",
            dim.id,
            "high"
          ) as DimText[])
        : [];

    if (canShowStrengthAndRiskTexts(dim, context)) {
      texts.strength = [...texts.strength, ...strengthTerms];
      texts.risk = [...texts.risk, ...riskTerms];
    }
    texts.behavior = [...texts.behavior, ...behaviorTerms];
  });

  return texts;
};

const getDimensionInsightsDescription = (
  t: (key: string, options?: any) => string,
  dictionary: ReadonlyArray<DimText | null | undefined> | undefined,
  dimension: DimensionWithScores,
  strengthDimensions: DimensionWithScores[],
  context: DimensionContext
) => {
  const strengthTexts = strengthDimensions.map((dim) => {
    const inContextScore =
      context === "user_in_team"
        ? (dim.score?.value || 0) - (dim.textsScore?.value || 0) > 0
          ? 100
          : 0
        : dim.textsScore?.value || 0;

    const value = getDimTextBucketValueFromScore(inContextScore);
    return getDictionaryTerms(dictionary, "strength_short", dim.id, value)[0];
  });

  const dimensionTagsText = strengthTexts
    .map((text) => getDimensionTag(text as DimText))
    .join(" and ");
  const personMentionText = getPersonMentionTag(dimension);

  const textParams = {
    dimensionTags: dimensionTagsText,
    person: personMentionText,
  };

  const textContext = includes(["user", "team"], context) ? context : "person";

  if (getIsPersonalityDimension(dimension.type as AssessmentSurveyType)) {
    return t(`dimensions.personality_insight_${textContext}`, textParams);
  }

  const bucket = getDimTextBucketValueFromScore(
    dimension.textsScore?.value || 0,
    true
  );
  const isInTeamPerson =
    context === "user_in_team" ||
    (context === "candidate" &&
      dimension?.allScores?.find((s) => s.obj_type === "team"));

  if (getIsValuesDimension(dimension.type as AssessmentSurveyType)) {
    const dimensionTagHigh =
      getDictionaryTerms(dictionary, "strength_short", dimension.id, "high")[0]
        ?.text || `dimension_${dimension.id}_strength_short_high`;
    const dimensionTagLow =
      getDictionaryTerms(dictionary, "strength_short", dimension.id, "low")[0]
        ?.text || `dimension_${dimension.id}_strength_short_low`;

    let textSelector = `dimensions.values_insight_${textContext}_${bucket}`;

    if (isInTeamPerson) {
      const personBucket = getDimTextBucketValueFromScore(
        dimension.score?.value || 0,
        true
      );
      const teamBucket = getDimTextBucketValueFromScore(
        dimension.averageScore?.value || 0,
        true
      );
      textSelector = `dimensions.values_insight_person_${personBucket}_team_${teamBucket}`;
    }

    return t(textSelector, {
      ...textParams,
      dimensionTagHigh,
      dimensionTagLow,
    });
  }

  if (getIsCompetenciesDimension(dimension.type as AssessmentSurveyType)) {
    const dimensionTag =
      getDictionaryTerms(dictionary, "strength_short", dimension.id, "high")[0]
        ?.text || "";

    let textSelector = `dimensions.competencies_insight_${textContext}_${bucket}`;

    if (isInTeamPerson) {
      const personBucket = getDimTextBucketValueFromScore(
        dimension.score?.value || 0,
        true
      );
      const teamBucket = getDimTextBucketValueFromScore(
        dimension.averageScore?.value || 0,
        true
      );
      textSelector = `dimensions.competencies_insight_person_${personBucket}_team_${teamBucket}`;
    }

    return t(textSelector, {
      ...textParams,
      dimensionTag,
    });
  }
};

const getDimensionDetailsTitles = (
  t: (key: string, options?: any) => string,
  dimension: DimensionWithScores,
  context: DimensionContext
) => {
  const group = toLower(dimension.group || "");
  const textContext = includes(["user", "team"], context) ? context : "person";
  const personMentionText = getPersonMentionTag(dimension);

  const textParams = {
    person: personMentionText,
  };

  return {
    strengthTitle: t(
      `dimensions.${group}_strength_title_${textContext}`,
      textParams
    ),
    riskTitle: t(`dimensions.${group}_risk_title_${textContext}`, textParams),
    actionTitle: t(`dimensions.${group}_action_title`, textParams),
    behaviorTitle: t(`dimensions.${group}_behavior_title`, textParams),
  };
};

interface DimensionDetailsTexts {
  insightsDescription: string;
  strengthTitle?: string;
  riskTitle?: string;
  actionTitle?: string;
  behaviorTitle?: string;
  strengthTexts?: DimText[];
  riskTexts?: DimText[];
  actionTexts?: DimText[];
  behaviorTexts?: DimText[];
}

export const useDimensionTextSelectors = (
  dimension: DimensionWithScores,
  subDimensions: DimensionWithScores[],
  context: DimensionContext = "user"
) => {
  const { t } = useTranslation();
  const { dictionary } = useDictionary();

  const strengthDims = getTopStrengthDimensions(
    dimension,
    subDimensions,
    context
  );
  const highestDeviationDim = first(strengthDims);

  const includeMid = getIsPersonalityDimension(
    dimension?.group as AssessmentSurveyType
  );

  const actionTextBucket = getDimTextBucketValueFromScore(
    context === "user_in_team"
      ? highestDeviationDim?.score?.value || 0
      : highestDeviationDim?.textsScore?.value || 0,
    includeMid
  );

  const actionTexts = highestDeviationDim
    ? getDictionaryTerms(
        dictionary,
        "action",
        highestDeviationDim?.id,
        actionTextBucket
      )
    : [];

  const titles = getDimensionDetailsTitles(t, dimension, context);
  const texts = getDimensionDetailsTexts(dictionary, strengthDims, context);

  const insightsDescription = getDimensionInsightsDescription(
    t,
    dictionary,
    dimension,
    strengthDims,
    context
  );

  return {
    insightsDescription,
    actionTexts,
    strengthTexts: texts.strength,
    riskTexts: texts.risk,
    behaviorTexts: texts.behavior,
    hasInsightTexts:
      texts.strength.length > 0 ||
      texts.risk.length > 0 ||
      texts.behavior.length > 0,
    ...titles,
  };
};
